import type { Community } from '@communities/community/domain';
import { CommunityGrid } from '@communities/community-groups/components';
import type { CommunityGroup } from '@communities/community-groups/domain';
import { SectionHeader } from '@core/components';
import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import { Section, PageHeader, Button, ButtonType, ButtonSize, OverlayMode } from '@soteria/design-system-ve';

import * as styles from './CommunityGroupHeaderSection.css';

interface CommunityGroupHeaderSectionProps {
  communityGroup: CommunityGroup;
  communities: Community[] | null;
  isCommunityGroupAdmin: boolean;
  onAddCommunity: () => void;
  onSelectCommunity: (community: Community) => void;
}

/**
 * Header section component for community group detail pages.
 * Renders a PageHeader with the group name and description,
 * followed by a communities grid section.
 */
function CommunityGroupHeaderSectionComponent({
  communityGroup,
  communities,
  isCommunityGroupAdmin,
  onAddCommunity,
  onSelectCommunity,
}: CommunityGroupHeaderSectionProps) {
  // Action buttons for header
  const actionButtons = isCommunityGroupAdmin ? (
    <Button buttonSize={ButtonSize.Large} buttonType={ButtonType.Brand} overlayMode={OverlayMode.Solid} onClick={onAddCommunity}>
      ADD COMMUNITY
    </Button>
  ) : undefined;

  // Communities grid component
  const communitiesGrid = (
    <div className={styles.communityGridStyle}>
      <CommunityGrid
        columnFit="auto-fill"
        communities={communities || []}
        emptyStateClassName={styles.emptyCommunitiesStateStyle}
        onSelectCommunity={onSelectCommunity}
      />
    </div>
  );

  return (
    <>
      <PageHeader data-testid="community-group-header-v2" subtitle={communityGroup.description || undefined} title={communityGroup.name}>
        {actionButtons}
      </PageHeader>

      {/* Communities Section */}
      <div className={styles.communitiesSectionV2}>
        <Section spacing="none">
          <SectionHeader description="Communities in this group." title="Communities" />
          {communitiesGrid}
        </Section>
      </div>
    </>
  );
}

export const CommunityGroupHeaderSection = withAppInsightsTracking(CommunityGroupHeaderSectionComponent, 'CommunityGroupHeaderSection');
